import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'
import { ArrowRight, Calendar, Clock, Tag } from 'lucide-react'
import { blogPosts } from '@/data/blog'

export function BlogHub() {
  // First post is featured
  const featuredPost = blogPosts[0]
  const otherPosts = blogPosts.slice(1)

  return (
    <>
      <SEO
        title="AI Watermark Removal Blog - Guides, Tutorials & News"
        description="Learn about AI watermarks, detection bypass, and image processing. Guides for SynthID, Midjourney, DALL-E, Stable Diffusion, and more."
        canonical="/blog"
        keywords="ai watermark blog, synthid guide, ai image detection, watermark removal tutorial"
      />

      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              AI Watermark Blog
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              In-depth guides, tutorials, and news about AI image watermarks and detection bypass
            </p>
          </div>

          {/* Featured Post */}
          {featuredPost && (
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="block mb-12 group"
            >
              <div className="card overflow-hidden p-0 hover:shadow-lg transition-shadow">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="aspect-video md:aspect-auto bg-gradient-to-br from-primary-100 to-indigo-200 flex items-center justify-center min-h-[250px]">
                    <div className="text-center p-6">
                      <span className="text-6xl mb-4 block">🔐</span>
                      <span className="text-primary-700 font-medium">Featured Guide</span>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full capitalize">
                        {featuredPost.category}
                      </span>
                      <span className="text-sm text-gray-500">{featuredPost.readTime} read</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="mt-3 text-gray-600">{featuredPost.description}</p>
                    <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(featuredPost.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      {featuredPost.updated && featuredPost.updated !== featuredPost.date && (
                        <span className="text-green-600">Updated recently</span>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {featuredPost.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 flex items-center gap-2 text-primary-600 font-medium group-hover:gap-3 transition-all">
                      Read Full Guide <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Other Posts */}
          {otherPosts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="card group hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-4 flex items-center justify-center">
                    <span className="text-4xl">📄</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded capitalize">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{post.description}</p>
                  <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Coming Soon Placeholder */}
          {otherPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">More articles coming soon...</p>
            </div>
          )}

          {/* Newsletter CTA */}
          <div className="mt-16 p-8 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-2xl text-center">
            <h2 className="text-2xl font-bold text-gray-900">Stay Updated</h2>
            <p className="mt-2 text-gray-600 max-w-xl mx-auto">
              Get notified about new guides on AI watermarks, detection bypass techniques, and platform policy updates.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              <button className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
